import React, { useCallback } from "react";
import { useIntl } from "react-intl";
import { Col, Row } from "antd";

import {
  put,
  deleteApi,
  checkHttpStatus,
} from "../../../modules/httpFunctions";
import UserOperation from "./userOperation";
import LoadTable from "../../app/generic/loadTable";
import CustomIcon from "../../app/generic/customIcon";
import { handleError } from "../../../modules/errorLogger";
import { error, success, warn } from "../../../modules/messages";
import { parseDateTime } from "../../../modules/consignmentFunctions";
import { customSorter, customSorterName } from "../../../modules/functions";

export default function Table({ onLoadUsers, oneTimeLogins }) {
  const intl = useIntl();

  const handleDropClick = useCallback(
    (oneTimeLoginId) => {
      deleteApi(`user/one-time-login/${oneTimeLoginId}`)
        .then((r) => {
          if (checkHttpStatus(r)) {
            success(
              intl.formatMessage({
                id: "oneTimeLogin.drop.success",
                defaultMessage: "Jednorázové přihlášení bylo úspěšně zrušeno.",
              })
            );
            onLoadUsers();
          } else if (r?.response?.status === 410) {
            warn(
              intl.formatMessage({
                id: "oneTimeLogin.drop.dropped",
                defaultMessage: "Již bylo zrušeno",
              })
            );
          } else {
            warn(
              intl.formatMessage({
                id: "oneTimeLogin.resend.unknown",
                defaultMessage: "Neznámý požadavek o přihlášení.",
              })
            );
          }
        })
        .catch((err) => {
          error(
            intl.formatMessage({
              id: "crud.deleteError",
              defaultMessage: "Při pokusu zrušení nastala chyba!",
            })
          );
          handleError(err);
        });
    },
    [intl, onLoadUsers]
  );

  const handleResendClick = useCallback(
    (oneTimeLoginId) => {
      put(`user/one-time-login/${oneTimeLoginId}`)
        .then((r) => {
          if (checkHttpStatus(r)) {
            success(
              intl.formatMessage({
                id: "oneTimeLogin.resend.success",
                defaultMessage: "SMS byla úspěšně znovuodeslána.",
              })
            );
          } else if (r?.response?.status === 410) {
            warn(
              intl.formatMessage({
                id: "oneTimeLogin.resend.limit",
                defaultMessage: "Byl překročen limit odeslání sms.",
              })
            );
          } else {
            warn(
              intl.formatMessage({
                id: "oneTimeLogin.resend.unknown",
                defaultMessage: "Neznámý požadavek o přihlášení.",
              })
            );
          }
          onLoadUsers();
        })
        .catch((err) => {
          handleError(err);
          error(
            intl.formatMessage({
              id: "oneTimeLogin.resend.error",
              defaultMessage: "Při pokusu o odeslání sms nastala chyba.",
            })
          );
        });
    },
    [intl, onLoadUsers]
  );

  const columns = [
    {
      key: "firstname",
      title: intl.formatMessage({
        id: "user.firstname",
        defaultMessage: "Jméno",
      }),
      dataIndex: "firstname",
      sorter: (a, b) => customSorter(a, b, "firstname"),
    },
    {
      key: "lastname",
      title: intl.formatMessage({
        id: "user.lastname",
        defaultMessage: "Příjmení",
      }),
      dataIndex: "lastname",
      sorter: (a, b) => customSorter(a, b, "lastname"),
    },
    {
      key: "username",
      title: intl.formatMessage({
        id: "oneTimeLogin.phone",
        defaultMessage: "Telefon",
      }),
      dataIndex: "username",
      sorter: (a, b) => customSorter(a, b, "username"),
    },
    {
      key: "email",
      title: intl.formatMessage({ id: "user.email", defaultMessage: "Email" }),
      dataIndex: "email",
      sorter: (a, b) => customSorter(a, b, "email"),
    },
    {
      key: "code",
      title: intl.formatMessage({
        id: "oneTimeLogin.code",
        defaultMessage: "Kód",
      }),
      dataIndex: "code",
    },
    {
      key: "lastLogin",
      title: intl.formatMessage({
        id: "oneTimeLogin.lastLogin",
        defaultMessage: "Datum přihlášení",
      }),
      dataIndex: "lastLogin",
      render: (lastLogin) => parseDateTime(lastLogin),
      sorter: (a, b) => customSorter(a, b, "lastLogin"),
    },
    {
      key: "passwordValidity",
      title: intl.formatMessage({
        id: "oneTimeLogin.validity",
        defaultMessage: "Stav",
      }),
      dataIndex: "passwordValidity",
      render: (
        passwordValidity,
        { invalid, deleter, lastLogin, oneTimeLoginId }
      ) => (
        <CustomIcon
          onClick={() =>
            !invalid && !lastLogin && handleResendClick(oneTimeLoginId)
          }
          color={invalid && !lastLogin && "red"}
          type={deleter ? "stop" : lastLogin || !invalid ? "check" : "close"}
          title={
            deleter
              ? intl.formatMessage(
                  {
                    id: "oneTimeLogin.deleted",
                    defaultMessage:
                      "Přihlášení bylo zrušeno. Platnost do {date}.",
                  },
                  {
                    date: parseDateTime(passwordValidity),
                  }
                )
              : lastLogin
              ? intl.formatMessage(
                  {
                    id: "oneTimeLogin.used",
                    defaultMessage: "Přihlášení bylo využito.",
                  },
                  {
                    date: parseDateTime(passwordValidity),
                  }
                )
              : invalid
              ? intl.formatMessage(
                  {
                    id: "oneTimeLogin.invalid",
                    defaultMessage: "Již neaktivní. Platné do {date}.",
                  },
                  {
                    date: parseDateTime(passwordValidity),
                  }
                )
              : intl.formatMessage(
                  {
                    id: "oneTimeLogin.valid",
                    defaultMessage:
                      "Platnost do {date}, kliknutím odešlete sms znovu a navýšíte čas pro přihlášení.",
                  },
                  {
                    date: parseDateTime(passwordValidity),
                  }
                )
          }
        />
      ),
    },
    {
      key: "inserter",
      title: intl.formatMessage({
        id: "oneTimeLogin.inserter",
        defaultMessage: "Vytvoření",
      }),
      dataIndex: "inserter",
      render: (inserter) => <UserOperation operation={inserter} />,
      sorter: (a, b) => customSorterName(a, b, "inserter", "dateTime"),
    },
    {
      key: "changer",
      title: intl.formatMessage({
        id: "oneTimeLogin.changer",
        defaultMessage: "Úprava",
      }),
      dataIndex: "changer",
      render: (changer) => <UserOperation operation={changer} />,
      sorter: (a, b) => customSorterName(a, b, "changer", "dateTime"),
    },
    {
      key: "deleter",
      title: intl.formatMessage({
        id: "oneTimeLogin.deleter",
        defaultMessage: "Storno",
      }),
      dataIndex: "deleter",
      render: (deleter, { invalid, lastLogin, oneTimeLoginId }) =>
        deleter ? (
          <UserOperation operation={deleter} />
        ) : invalid || lastLogin ? (
          "--"
        ) : (
          <CustomIcon
            color="red"
            type="delete"
            title={intl.formatMessage({
              id: "oneTimeLogin.drop",
              defaultMessage: "Kliknutím zrušíte přihlášení",
            })}
            onClick={() => handleDropClick(oneTimeLoginId)}
          />
        ),
      sorter: (a, b) => customSorterName(a, b, "deleter", "dateTime"),
    },
  ];

  return (
    <Row>
      <Col>
        <LoadTable
          values={oneTimeLogins}
          what={intl.formatMessage({
            id: "oneTimeLogin.whom",
            defaultMessage: "jednorázových přihlášení",
          })}
          columns={columns}
          className="no-pointer"
          onLoadValues={onLoadUsers}
          emptyText={intl.formatMessage({
            id: "oneTimeLogin.noUsers",
            defaultMessage: "tento klient nemá jednorázové přihlášení",
          })}
        />
      </Col>
    </Row>
  );
}
